import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { myMedicationService } from '../lib/myMedicationService';
import { isMedicationActive } from '../utils/dateHelpers';
import { getDiseaseProfile, upsertDiseaseProfile, type DiseaseProfile } from '../lib/diseaseProfileService';

interface AccountPageProps {
  user: { id: string; email?: string; user_metadata?: { full_name?: string } };
  rem: (px: number) => string;
  fontSize: { base: string; sm: string; lg: string };
  borderRadius: string;
  onScheduleFixed?: () => void | Promise<void>;
}

export const AccountPage: React.FC<AccountPageProps> = ({
  user,
  rem,
  fontSize,
  borderRadius,
  onScheduleFixed
}) => {
  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMessage, setNameMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [scheduleFixLoading, setScheduleFixLoading] = useState(false);
  const [scheduleFixMessage, setScheduleFixMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [diseaseProfile, setDiseaseProfile] = useState<Partial<DiseaseProfile>>({});
  const [diseaseProfileLoading, setDiseaseProfileLoading] = useState(false);
  const [diseaseProfileSaving, setDiseaseProfileSaving] = useState(false);
  const [diseaseProfileMessage, setDiseaseProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const canDelete = deleteConfirm.toUpperCase() === 'USUŃ' || deleteConfirm.toUpperCase() === 'DELETE';

  const WZJG_LOCATIONS = ['Proctitis', 'Lewostronne', 'Rozległe', 'Pancolitis'];
  const CROHN_LOCATIONS = ['Jelito cienkie', 'Jelito grube', 'Odbytnica', 'Górny odcinek przewodu pokarmowego', 'Mieszane'];
  const DIET_OPTIONS = ['Normalna', 'Low-FODMAP', 'SCD', 'Bezglutenowa', 'Bezlaktozowa', 'Inna'];

  useEffect(() => {
    setFullName(user?.user_metadata?.full_name || '');
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    setDiseaseProfileLoading(true);
    getDiseaseProfile(user.id)
      .then((profile) => {
        if (profile) {
          setDiseaseProfile({
            disease_type: profile.disease_type,
            diagnosis_date: profile.diagnosis_date,
            diagnosed_by: profile.diagnosed_by,
            disease_location: profile.disease_location,
            disease_location_other: profile.disease_location_other,
            last_flare_date: profile.last_flare_date,
            last_colonoscopy_date: profile.last_colonoscopy_date,
            avg_bowel_movements_remission: profile.avg_bowel_movements_remission,
            has_stoma: profile.has_stoma,
            bowel_surgeries: profile.bowel_surgeries,
            bowel_surgeries_description: profile.bowel_surgeries_description,
            food_allergies: profile.food_allergies,
            diet: profile.diet,
            diet_other: profile.diet_other,
            doctor_name: profile.doctor_name,
            hospital: profile.hospital
          });
        } else {
          setDiseaseProfile({ disease_location: [], diet: [] });
        }
      })
      .finally(() => setDiseaseProfileLoading(false));
  }, [user?.id]);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameLoading(true);
    setNameMessage(null);
    try {
      const { error } = await supabase.auth.updateUser({ data: { full_name: fullName } });
      if (error) throw error;
      setNameMessage({ type: 'success', text: 'Imię zapisane.' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd zapisu';
      setNameMessage({ type: 'error', text: msg });
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Hasło musi mieć minimum 8 znaków' });
      return;
    }
    if (newPassword !== repeatPassword) {
      setPasswordMessage({ type: 'error', text: 'Hasła muszą być identyczne' });
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMessage({ type: 'success', text: 'Hasło zmienione.' });
      setNewPassword('');
      setRepeatPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd zmiany hasła';
      setPasswordMessage({ type: 'error', text: msg });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveDiseaseProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setDiseaseProfileSaving(true);
    setDiseaseProfileMessage(null);
    try {
      await upsertDiseaseProfile(user.id, diseaseProfile);
      setDiseaseProfileMessage({ type: 'success', text: 'Historia choroby zapisana.' });
    } catch (err: unknown) {
      const rawMsg = err instanceof Error ? err.message : 'Nie udało się zapisać.';
      const isTableMissing = /does not exist|relation.*not found/i.test(rawMsg);
      const msg = isTableMissing
        ? 'Tabela disease_profiles nie istnieje. Uruchom migrację SQL w Supabase (supabase/migrations/008_disease_profiles.sql).'
        : rawMsg;
      setDiseaseProfileMessage({ type: 'error', text: msg });
    } finally {
      setDiseaseProfileSaving(false);
    }
  };

  const toggleArrayItem = (arr: string[], item: string) =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const handleFixSchedule = async () => {
    setScheduleFixLoading(true);
    setScheduleFixMessage(null);
    try {
      const medications = await myMedicationService.getAll();
      const activeMedications = medications.filter((m) => isMedicationActive(m));

      const now = new Date();
      const startOfYear = `${now.getFullYear()}-01-01`;
      const endOfNextYear = new Date(now.getFullYear() + 1, 11, 31);
      const endDate = `${endOfNextYear.getFullYear()}-${String(endOfNextYear.getMonth() + 1).padStart(2, '0')}-${String(endOfNextYear.getDate()).padStart(2, '0')}`;

      for (const med of activeMedications) {
        await myMedicationService.deleteAllScheduleEntriesForMedication(med.id);
        const medStart = med.startDate || startOfYear;
        console.log('[handleFixSchedule] generateScheduleEntries', { medId: med.id, medicationName: med.name, startDate: medStart, endDate, medStartDate: med.startDate, medEndDate: med.endDate });
        await myMedicationService.generateScheduleEntries(med, medStart, endDate);
      }

      setScheduleFixMessage({ type: 'success', text: 'Harmonogram zaktualizowany' });
      await onScheduleFixed?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd naprawiania harmonogramu';
      setScheduleFixMessage({ type: 'error', text: msg });
    } finally {
      setScheduleFixLoading(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canDelete) return;
    setDeleteLoading(true);
    setDeleteMessage(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Brak sesji');

      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania konta';
      setDeleteMessage({ type: 'error', text: msg });
    } finally {
      setDeleteLoading(false);
    }
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: rem(32),
    paddingBottom: rem(24),
    borderBottom: '1px solid #E5E5E5'
  };
  const lastSectionStyle: React.CSSProperties = { ...sectionStyle, borderBottom: 'none' };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: rem(12),
    border: '1px solid #E5E5E5',
    borderRadius: rem(12),
    fontSize: fontSize.base,
    boxSizing: 'border-box',
    maxWidth: '400px'
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: rem(8),
    fontWeight: 600,
    color: '#1A1A1A',
    fontSize: fontSize.base
  };
  const buttonStyle: React.CSSProperties = {
    padding: `${rem(12)} ${rem(24)}`,
    background: 'linear-gradient(to right, #6B7FFF, #8B5CF6)',
    color: 'white',
    border: 'none',
    borderRadius: rem(12),
    fontSize: fontSize.base,
    fontWeight: 600,
    cursor: 'pointer'
  };
  const buttonDangerStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#dc2626',
    color: 'white'
  };

  return (
    <div>
      {/* Dane konta */}
      <section style={sectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(16) }}>
          Dane konta
        </h2>
        <p style={{ marginBottom: rem(8), color: '#666', fontSize: fontSize.sm }}>Email: {user?.email}</p>
        <p style={{ marginBottom: rem(16), color: '#999', fontSize: fontSize.sm }}>
          ID: {user?.id}
        </p>

        <form onSubmit={handleSaveName}>
          <label style={labelStyle}>Imię</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={inputStyle}
            placeholder="Twoje imię"
          />
          <button
            type="submit"
            disabled={nameLoading}
            style={{ ...buttonStyle, marginTop: rem(12) }}
          >
            {nameLoading ? 'Zapisywanie...' : 'Zapisz imię'}
          </button>
          {nameMessage && (
            <p
              style={{
                marginTop: rem(8),
                color: nameMessage.type === 'success' ? '#059669' : '#dc2626',
                fontSize: fontSize.sm
              }}
            >
              {nameMessage.text}
            </p>
          )}
        </form>
      </section>

      {/* Historia choroby */}
      <section style={sectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(16) }}>
          Historia choroby
        </h2>
        {diseaseProfileLoading ? (
          <p style={{ color: '#666', fontSize: fontSize.sm }}>Ładowanie...</p>
        ) : (
          <form onSubmit={handleSaveDiseaseProfile}>
            {/* 1. Diagnoza */}
            <div style={{ marginBottom: rem(24) }}>
              <h3 style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: rem(12), color: '#333' }}>1. Diagnoza</h3>
              <label style={labelStyle}>Typ choroby</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(12), marginBottom: rem(16) }}>
                {['WZJG', 'Choroba Leśniowskiego-Crohna', 'Inne'].map((t) => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="disease_type"
                      checked={diseaseProfile.disease_type === t}
                      onChange={() => setDiseaseProfile((p) => ({ ...p, disease_type: t }))}
                    />
                    <span style={{ fontSize: fontSize.base }}>{t}</span>
                  </label>
                ))}
              </div>
              <label style={labelStyle}>Data rozpoznania</label>
              <input
                type="date"
                value={diseaseProfile.diagnosis_date || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, diagnosis_date: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
              <label style={labelStyle}>Kto postawił diagnozę</label>
              <input
                type="text"
                value={diseaseProfile.diagnosed_by || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, diagnosed_by: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
                placeholder="np. lekarz rodzinny, gastroenterolog"
              />
            </div>

            {/* 2. Lokalizacja zmian */}
            <div style={{ marginBottom: rem(24) }}>
              <h3 style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: rem(12), color: '#333' }}>2. Lokalizacja zmian</h3>
              {diseaseProfile.disease_type === 'WZJG' && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8), marginBottom: rem(12) }}>
                    {WZJG_LOCATIONS.map((loc) => (
                      <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={(diseaseProfile.disease_location || []).includes(loc)}
                          onChange={() => setDiseaseProfile((p) => ({ ...p, disease_location: toggleArrayItem(p.disease_location || [], loc) }))}
                        />
                        <span style={{ fontSize: fontSize.sm }}>{loc}</span>
                      </label>
                    ))}
                  </div>
                  <label style={labelStyle}>Inne</label>
                  <input
                    type="text"
                    value={diseaseProfile.disease_location_other || ''}
                    onChange={(e) => setDiseaseProfile((p) => ({ ...p, disease_location_other: e.target.value || null }))}
                    style={{ ...inputStyle, marginBottom: rem(16) }}
                  />
                </>
              )}
              {diseaseProfile.disease_type === 'Choroba Leśniowskiego-Crohna' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8), marginBottom: rem(16) }}>
                  {CROHN_LOCATIONS.map((loc) => (
                    <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(diseaseProfile.disease_location || []).includes(loc)}
                        onChange={() => setDiseaseProfile((p) => ({ ...p, disease_location: toggleArrayItem(p.disease_location || [], loc) }))}
                      />
                      <span style={{ fontSize: fontSize.sm }}>{loc}</span>
                    </label>
                  ))}
                </div>
              )}
              {diseaseProfile.disease_type === 'Inne' && (
                <>
                  <label style={labelStyle}>Opis lokalizacji</label>
                  <input
                    type="text"
                    value={diseaseProfile.disease_location_other || ''}
                    onChange={(e) => setDiseaseProfile((p) => ({ ...p, disease_location_other: e.target.value || null }))}
                    style={{ ...inputStyle, marginBottom: rem(16) }}
                  />
                </>
              )}
              {!diseaseProfile.disease_type && (
                <p style={{ color: '#999', fontSize: fontSize.sm, marginBottom: rem(16) }}>Wybierz typ choroby powyżej</p>
              )}
            </div>

            {/* 3. Aktywność choroby */}
            <div style={{ marginBottom: rem(24) }}>
              <h3 style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: rem(12), color: '#333' }}>3. Aktywność choroby</h3>
              <label style={labelStyle}>Data ostatniego rzutu</label>
              <input
                type="date"
                value={diseaseProfile.last_flare_date || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, last_flare_date: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
              <label style={labelStyle}>Data ostatniej kolonoskopii</label>
              <input
                type="date"
                value={diseaseProfile.last_colonoscopy_date || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, last_colonoscopy_date: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
            </div>

            {/* 4. Dane ogólne */}
            <div style={{ marginBottom: rem(24) }}>
              <h3 style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: rem(12), color: '#333' }}>4. Dane ogólne</h3>
              <label style={labelStyle}>Średnia liczba wypróżnień na dobę w remisji</label>
              <input
                type="number"
                min={0}
                value={diseaseProfile.avg_bowel_movements_remission ?? ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, avg_bowel_movements_remission: e.target.value ? parseInt(e.target.value, 10) : null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
              <label style={labelStyle}>Czy jest stomia</label>
              <div style={{ display: 'flex', gap: rem(16), marginBottom: rem(16) }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="has_stoma"
                    checked={diseaseProfile.has_stoma === true}
                    onChange={() => setDiseaseProfile((p) => ({ ...p, has_stoma: true }))}
                  />
                  <span>Tak</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="has_stoma"
                    checked={diseaseProfile.has_stoma === false}
                    onChange={() => setDiseaseProfile((p) => ({ ...p, has_stoma: false }))}
                  />
                  <span>Nie</span>
                </label>
              </div>
              <label style={labelStyle}>Operacje jelitowe</label>
              <div style={{ display: 'flex', gap: rem(16), marginBottom: rem(12) }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="bowel_surgeries"
                    checked={diseaseProfile.bowel_surgeries === true}
                    onChange={() => setDiseaseProfile((p) => ({ ...p, bowel_surgeries: true }))}
                  />
                  <span>Tak</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="bowel_surgeries"
                    checked={diseaseProfile.bowel_surgeries === false}
                    onChange={() => setDiseaseProfile((p) => ({ ...p, bowel_surgeries: false }))}
                  />
                  <span>Nie</span>
                </label>
              </div>
              {diseaseProfile.bowel_surgeries === true && (
                <>
                  <label style={labelStyle}>Opis operacji</label>
                  <input
                    type="text"
                    value={diseaseProfile.bowel_surgeries_description || ''}
                    onChange={(e) => setDiseaseProfile((p) => ({ ...p, bowel_surgeries_description: e.target.value || null }))}
                    style={{ ...inputStyle, marginBottom: rem(16) }}
                    placeholder="Opisz przeprowadzone operacje"
                  />
                </>
              )}
              <label style={labelStyle}>Alergie pokarmowe i nietolerancje</label>
              <input
                type="text"
                value={diseaseProfile.food_allergies || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, food_allergies: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
                placeholder="np. gluten, laktoza"
              />
              <label style={labelStyle}>Dieta</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8), marginBottom: rem(12) }}>
                {DIET_OPTIONS.map((d) => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={(diseaseProfile.diet || []).includes(d)}
                      onChange={() => setDiseaseProfile((p) => ({ ...p, diet: toggleArrayItem(p.diet || [], d) }))}
                    />
                    <span style={{ fontSize: fontSize.sm }}>{d}</span>
                  </label>
                ))}
              </div>
              {(diseaseProfile.diet || []).includes('Inna') && (
                <>
                  <label style={labelStyle}>Inna dieta – opis</label>
                  <input
                    type="text"
                    value={diseaseProfile.diet_other || ''}
                    onChange={(e) => setDiseaseProfile((p) => ({ ...p, diet_other: e.target.value || null }))}
                    style={{ ...inputStyle, marginBottom: rem(16) }}
                  />
                </>
              )}
            </div>

            {/* 5. Dane kontaktowe lekarzy */}
            <div style={{ marginBottom: rem(24) }}>
              <h3 style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: rem(12), color: '#333' }}>5. Dane kontaktowe lekarzy</h3>
              <label style={labelStyle}>Imię i nazwisko gastroenterologa prowadzącego</label>
              <input
                type="text"
                value={diseaseProfile.doctor_name || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, doctor_name: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
              <label style={labelStyle}>Szpital/przychodnia</label>
              <input
                type="text"
                value={diseaseProfile.hospital || ''}
                onChange={(e) => setDiseaseProfile((p) => ({ ...p, hospital: e.target.value || null }))}
                style={{ ...inputStyle, marginBottom: rem(16) }}
              />
            </div>

            <button type="submit" disabled={diseaseProfileSaving} style={{ ...buttonStyle, marginTop: rem(8) }}>
              {diseaseProfileSaving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
            {diseaseProfileMessage && (
              <p style={{ marginTop: rem(8), color: diseaseProfileMessage.type === 'success' ? '#059669' : '#dc2626', fontSize: fontSize.sm }}>
                {diseaseProfileMessage.text}
              </p>
            )}
          </form>
        )}
      </section>

      {/* Bezpieczeństwo */}
      <section style={sectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(16) }}>
          Bezpieczeństwo
        </h2>
        <form onSubmit={handleChangePassword}>
          <label style={labelStyle}>Nowe hasło</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={8}
            style={{ ...inputStyle, marginBottom: rem(12) }}
            placeholder="Minimum 8 znaków"
          />
          <label style={labelStyle}>Powtórz hasło</label>
          <input
            type="password"
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            minLength={8}
            style={{ ...inputStyle, marginBottom: rem(12) }}
            placeholder="Powtórz hasło"
          />
          <button
            type="submit"
            disabled={passwordLoading}
            style={buttonStyle}
          >
            {passwordLoading ? 'Zapisywanie...' : 'Zmień hasło'}
          </button>
          {passwordMessage && (
            <p
              style={{
                marginTop: rem(8),
                color: passwordMessage.type === 'success' ? '#059669' : '#dc2626',
                fontSize: fontSize.sm
              }}
            >
              {passwordMessage.text}
            </p>
          )}
        </form>
      </section>

      {/* Ustawienia */}
      <section style={sectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(16) }}>
          Ustawienia
        </h2>
        <p style={{ marginBottom: rem(8), color: '#666', fontSize: fontSize.sm }}>
          Kliknij jeśli daty leków wyświetlają się nieprawidłowo.
        </p>
        <button
          type="button"
          onClick={handleFixSchedule}
          disabled={scheduleFixLoading}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            background: scheduleFixLoading ? '#ccc' : '#f0f0f0',
            color: '#666',
            border: '1px solid #E5E5E5',
            borderRadius: rem(8),
            fontSize: fontSize.sm,
            cursor: scheduleFixLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {scheduleFixLoading ? 'Naprawianie...' : 'Napraw harmonogram leków'}
        </button>
        {scheduleFixMessage && (
          <p
            style={{
              marginTop: rem(8),
              color: scheduleFixMessage.type === 'success' ? '#059669' : '#dc2626',
              fontSize: fontSize.sm
            }}
          >
            {scheduleFixMessage.text}
          </p>
        )}
      </section>

      {/* Wylogowanie */}
      <section style={sectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(16) }}>
          Sesja
        </h2>
        <button
          onClick={handleLogout}
          style={{
            ...buttonStyle,
            background: 'transparent',
            color: '#666',
            border: '1px solid #E5E5E5'
          }}
        >
          Wyloguj
        </button>
      </section>

      {/* Usuń konto */}
      <section style={lastSectionStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#dc2626', marginBottom: rem(16) }}>
          Usuń konto
        </h2>
        <p style={{ marginBottom: rem(12), color: '#666', fontSize: fontSize.sm }}>
          Ta operacja usunie Twoje konto i wszystkie dane. Wpisz <strong>USUŃ</strong> lub <strong>DELETE</strong>, aby odblokować przycisk.
        </p>
        <form onSubmit={handleDeleteAccount}>
          <input
            type="text"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            style={{ ...inputStyle, borderColor: deleteConfirm ? '#dc2626' : '#E5E5E5', maxWidth: '300px' }}
            placeholder="Wpisz USUŃ lub DELETE"
          />
          <button
            type="submit"
            disabled={!canDelete || deleteLoading}
            style={{
              ...buttonDangerStyle,
              marginTop: rem(12),
              opacity: canDelete ? 1 : 0.5,
              cursor: canDelete ? 'pointer' : 'not-allowed'
            }}
          >
            {deleteLoading ? 'Usuwanie...' : 'Usuń konto'}
          </button>
          {deleteMessage && (
            <p
              style={{
                marginTop: rem(8),
                color: deleteMessage.type === 'success' ? '#059669' : '#dc2626',
                fontSize: fontSize.sm
              }}
            >
              {deleteMessage.text}
            </p>
          )}
        </form>
      </section>
    </div>
  );
};
